import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Divider,
  Alert
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const PaymentConfirmation = () => {
  const [status, setStatus] = useState('loading');
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        // Try to get payment intent ID from both URL parameters and location state
        const urlParams = new URLSearchParams(location.search);
        let paymentIntentId = urlParams.get('payment_intent');
        
        // If not in URL, check location state
        if (!paymentIntentId && location.state && location.state.paymentIntent) {
          console.log('Found payment intent in location state:', location.state.paymentIntent);
          paymentIntentId = location.state.paymentIntent;
        }
        
        if (!paymentIntentId) {
          console.error('Payment intent not found in URL or state');
          setStatus('error');
          setError('Payment information not found. Please contact support if you believe this is an error.');
          return;
        }
        
        // Verify payment status
        console.log('Verifying payment status for:', paymentIntentId);
        const response = await axios.get(`${API_URL}/api/payment/status/${paymentIntentId}`);
        
        if (response.data.status === 'succeeded') {
          setStatus('success');
          setPaymentData(response.data);
        } else {
          setStatus('error');
          setError('Payment was not completed successfully. Please try again or contact support.');
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        setStatus('error');
        setError('Unable to verify payment status. Please contact support if your payment was completed.');
      }
    };
    
    checkPaymentStatus();
  }, [location.search, location.state]);
  
  const handleGoToLogin = () => {
    navigate('/login');
  };
  
  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };
  
  if (status === 'loading') {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        minHeight="60vh"
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }}>
          Verifying payment...
        </Typography>
      </Box>
    );
  }
  
  if (status === 'error') {
    return (
      <Box 
        display="flex" 
        flexDirection="column" 
        alignItems="center" 
        justifyContent="center" 
        maxWidth="md" 
        mx="auto" 
        my={4}
      >
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Box display="flex" justifyContent="center" mt={3}>
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleGoToLogin}
            >
              Go to Login
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }
  
  return (
    <Box 
      display="flex" 
      flexDirection="column" 
      alignItems="center" 
      justifyContent="center" 
      maxWidth="md" 
      mx="auto" 
      my={4}
    >
      <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
        <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
          <Typography variant="h4" align="center" gutterBottom sx={{ mt: 2 }}>
            Payment Successful!
          </Typography>
          <Typography variant="body1" align="center" color="text.secondary">
            Your account has been created and your subscription is now active.
          </Typography>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        {paymentData && (
          <Box mb={4}>
            <Typography variant="h6" gutterBottom>
              Subscription Details
            </Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Plan:</Typography>
              <Typography variant="body2">{paymentData.planName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Amount:</Typography>
              <Typography variant="body2">${paymentData.amount} (annual)</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">Payment date:</Typography>
              <Typography variant="body2">{new Date(paymentData.paymentDate).toLocaleDateString()}</Typography>
            </Box>
          </Box>
        )}
        
        <Box display="flex" justifyContent="center">
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            onClick={handleGoToLogin}
            sx={{ minWidth: 200 }}
          >
            Sign In to Your Account
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default PaymentConfirmation; 